import React, { useContext } from "react";
import { Box, Button, Typography } from "@mui/material";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import { DataContext } from "../context/DataContext";
import { getTokens } from "../theme";

/**
 * Estado vacio de listados y tablas.
 *
 * Antes era un icono de base de datos de 100px temblando (`fa-shake`) con un
 * "No hay datos" en rojo tamaño h4 y parpadeando (`fa-fade`): gritaba error
 * cuando en realidad una lista vacia es un estado normal. Ahora es sobrio y usa
 * los tokens del tema, asi que funciona en claro y oscuro.
 *
 * Todas las props son opcionales: las 41 pantallas que ya lo usan como
 * `<NoData />` siguen funcionando sin cambios.
 *
 * @param {string} titulo   Encabezado. Por defecto "No hay datos".
 * @param {string} mensaje  Linea secundaria explicando el vacio.
 * @param {node}   icono    Icono a mostrar. Por defecto una bandeja vacia.
 * @param {string} accion   Texto del boton opcional.
 * @param {func}   onAccion Handler del boton; sin el, el boton no se dibuja.
 */
const NoData = ({ titulo = "No hay datos", mensaje, icono, accion, onAccion }) => {
  const { isDarkMode } = useContext(DataContext);
  const t = getTokens(isDarkMode);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        padding: "48px 24px",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "18px",
          backgroundColor: t.field,
          border: `1px solid ${t.fieldLine}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: t.mut2,
          marginBottom: "14px",
        }}
      >
        {icono || <InboxRoundedIcon sx={{ fontSize: 30 }} />}
      </Box>

      <Typography
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 600,
          fontSize: 16,
          color: "text.primary",
        }}
      >
        {titulo}
      </Typography>

      {mensaje ? (
        <Typography sx={{ fontSize: 13.5, color: "text.disabled", maxWidth: 380 }}>
          {mensaje}
        </Typography>
      ) : null}

      {accion && onAccion ? (
        <Button variant="contained" color="primary" onClick={onAccion} sx={{ marginTop: "16px" }}>
          {accion}
        </Button>
      ) : null}
    </Box>
  );
};

export default NoData;
